import { useEffect, useRef, useState } from "react";
import { api, getActiveArcherId } from "../lib/api";
import { localISODate } from "../lib/dates";
import { ErrorMessage } from "./ErrorMessage";
import { Modal } from "./Modal";

// ── Arrow-log quick-record bottom sheet (M9.5 #1810) ────────────────────────
//
// "Log arrows" — records unscored practice volume (warm-up, blind bale, form
// work) as an arrow-log entry: a count on a day, no clock. Date-only by
// design; the time-range path is the Phase-2 live counter, not this sheet.
//
// Reused without modification by the post-round nudge (#1811) and the History
// integration (#1812) via the props contract:
//   open / onClose / onSaved(savedEntry)
//   initial  — optional {date, count, activity, label} prefill
//   editId   — when set, Save PATCHes that arrow-log id instead of POSTing
//   archerId — explicit owner; defaults to the active archer

const PRESETS = [3, 6, 30, 60];
const STEP = 1; // stepper granularity

// Pills map to the backend activity slugs; free text (label) only behind Other.
// Exported so the counter save sheet (#1813) reuses the exact same label set.
export const ACTIVITIES = [
  { slug: "blind_bale", label: "Blind bale" },
  { slug: "warmup", label: "Warm-up" },
  { slug: "form", label: "Form work" },
  { slug: "other", label: "Other…" },
];

export function ArrowLogSheet({ open, onClose, onSaved, initial, editId, archerId }) {
  const [count, setCount] = useState(0);
  const [date, setDate] = useState(() => localISODate());
  const [activity, setActivity] = useState(null);
  const [label, setLabel] = useState("");
  const [typing, setTyping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const today = localISODate();

  // (Re)initialize the form each time the sheet opens — from `initial` when
  // given (nudge passes the round's date; History edit passes the full entry).
  // Only on the closed→open transition: callers may pass `initial` as an inline
  // object literal, and re-running on every identity change would wipe the
  // user's in-progress draft mid-edit.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setCount(initial?.count ?? 0);
      setDate(initial?.date ?? localISODate());
      setActivity(initial?.activity ?? null);
      setLabel(initial?.label ?? "");
      setTyping(false);
      setSaving(false);
      setError(null);
    }
    wasOpen.current = open;
  }, [open, initial]);

  // Escape, aria-modal, focus trap and focus restoration come from <Modal>,
  // which is mounted only while `open` — so its listeners live exactly as long
  // as the sheet does.

  if (!open) return null;

  // Weekday chips for the last 7 days (Today, Yday, then weekday names).
  const dayChips = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const iso = localISODate(d);
    const name =
      i === 0 ? "Today" : i === 1 ? "Yday" : d.toLocaleDateString("en-US", { weekday: "short" });
    return { iso, name };
  });

  function handleDateChange(value) {
    if (!value) return;
    if (value > today) return; // future dates blocked
    setDate(value);
  }

  function commitTyped(value) {
    const n = Math.max(0, Math.floor(Number(value) || 0));
    setCount(n);
    setTyping(false);
  }

  async function handleSave() {
    const payload = {
      date,
      count,
      activity: activity ?? null,
      // Free text only rides along behind "Other…" — the named pills ARE the label.
      label: activity === "other" ? label.trim() || null : null,
    };
    setSaving(true);
    setError(null);
    try {
      const saved =
        editId != null
          ? await api.updateArrowLog(editId, payload)
          : await api.createArrowLog(archerId ?? getActiveArcherId(), payload);
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setError(err?.message || "Could not save arrow log.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal variant="arrowLog" onClose={onClose} labelledBy="als-title">
      <div className="als-handle" />
      <div className="als-head">
        <div>
          <div className="als-title" id="als-title">Log arrows</div>
          <div className="als-subtitle">Unscored volume — warm-up, blind bale, form work.</div>
        </div>
        <button type="button" className="als-close" onClick={onClose} aria-label="Close">✕</button>
      </div>

      <div className="als-body">
        {/* ARROWS */}
        <div className="als-section">
          <div className="als-label">Arrows</div>
          <div className="als-presets">
            {PRESETS.map((n) => (
              <button
                type="button"
                key={n}
                className={"als-preset" + (count === n ? " als-preset--on" : "")}
                aria-pressed={count === n}
                onClick={() => { setCount(n); setTyping(false); }}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="als-stepper">
            <button
              type="button"
              className="als-step"
              aria-label="Decrease by 1"
              disabled={count <= 0}
              onClick={() => setCount((c) => Math.max(0, c - STEP))}
            >
              −
            </button>
            {typing ? (
              <input
                className="als-count-input"
                type="number"
                inputMode="numeric"
                min="0"
                aria-label="Arrow count"
                autoFocus
                defaultValue={count || ""}
                onBlur={(e) => commitTyped(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commitTyped(e.target.value); }}
              />
            ) : (
              <button
                type="button"
                className="als-count"
                aria-label="Type arrow count"
                onClick={() => setTyping(true)}
              >
                {count}
              </button>
            )}
            <button
              type="button"
              className="als-step"
              aria-label="Increase by 1"
              onClick={() => setCount((c) => c + STEP)}
            >
              +
            </button>
          </div>
        </div>

        {/* DATE — date-only by design; the time-range path is the Phase-2 counter */}
        <div className="als-section">
          <div className="als-label">Date</div>
          <div className="als-days">
            {dayChips.map((d) => (
              <button
                type="button"
                key={d.iso}
                className={"als-day" + (date === d.iso ? " als-day--on" : "")}
                aria-pressed={date === d.iso}
                onClick={() => setDate(d.iso)}
              >
                {d.name}
              </button>
            ))}
          </div>
          <input
            className="bs-input als-date"
            type="date"
            aria-label="Date"
            value={date}
            max={today}
            onChange={(e) => handleDateChange(e.target.value)}
          />
        </div>

        {/* LABEL */}
        <div className="als-section">
          <div className="als-label">Label</div>
          <div className="als-pills">
            {ACTIVITIES.map((a) => (
              <button
                type="button"
                key={a.slug}
                className={"als-pill" + (activity === a.slug ? " als-pill--on" : "")}
                aria-pressed={activity === a.slug}
                onClick={() => setActivity((cur) => (cur === a.slug ? null : a.slug))}
              >
                {a.label}
              </button>
            ))}
          </div>
          {activity === "other" && (
            <input
              className="bs-input"
              type="text"
              aria-label="Custom label"
              placeholder="What were you working on?"
              maxLength={80}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          )}
        </div>

        <ErrorMessage message={error} />

        <button
          type="button"
          className="als-save"
          disabled={count < 1 || saving}
          onClick={handleSave}
        >
          {saving ? "Saving…" : `Save · ${count} arrow${count === 1 ? "" : "s"}`}
        </button>
      </div>
    </Modal>
  );
}
